import { axios } from '../util/axios'
import platform from '../util/platform'

const API_ENDPOINT_V2_OVERRIDE = '/v2/phone/override'
const API_ENDPOINT_V0_VALIDATION_OVERRIDE = '/v0/validation/pincode/override'

/**
 * Sends a request to override the phone PIN code SMS for phone validation flow.
 *
 * @param {string} phone - The phone number to validate.
 * @param {string} type - The type of validation or operation.
 * @param {string} code - The PIN code to override.
 * @param {string} issuer - The issuer of the request.
 * @param {string} token - The authorization token for the request.
 * @returns {Promise} Resolves with the API response or rejects with an error message.
 */
export const overridePhonePinCodeSMS = (phone, type, code, issuer, token) => {
  if (!token)
    return new Promise((resolve, reject) => {
      return reject('No token')
    })

  const bodyRequest = { phone, platform, type, code, issuer, attemptsCheck: true }

  return axios.post(API_ENDPOINT_V2_OVERRIDE, bodyRequest, {
    headers: {
      Authorization: token,
    },
    withCredentials: true,
  })
}

/**
 * Overrides the phone PIN code SMS validation by sending a request to the API.
 *
 * @param {string} code - The PIN code to validate.
 * @param {string} token - The authentication token required for the request.
 * @returns {Promise<Object>} A promise that resolves with the API response or rejects with an error message.
 */
export const overrideValidationPhonePinCodeSMS = (code, token) => {
  if (!token)
    return new Promise((resolve, reject) => {
      return reject('No token')
    })

  const bodyRequest = { platform, code, token }

  return axios.post(API_ENDPOINT_V0_VALIDATION_OVERRIDE, bodyRequest, {
    withCredentials: true,
  })
}

/**
 * Sends an SMS to the specified phone number for phone validation flow.
 *
 * @param {string} phone - The phone number to which the SMS should be sent.
 * @returns {Promise} Axios promise representing the HTTP request.
 */
export const sendSMS = phone =>
  axios.post(
    '/v1/phone/validation/pincode/send/sms',
    {
      phone,
      platform,
    },
    {
      withCredentials: true,
    }
  )

/**
 * Sends an SMS verification code to the specified phone number using the v2 API.
 *
 * @param {string} phone - The phone number to which the SMS should be sent.
 * @returns {Promise} Axios promise representing the HTTP request.
 */
export const sendSMSV2 = phone =>
  axios.post(
    '/v2/pincode/send/sms',
    {
      phone,
      platform,
    },
    {
      withCredentials: true,
    }
  )

/**
 * Sends an SMS verification code to the specified phone number using the v4 API.
 *
 * @param {string} phone - The phone number to send the SMS to.
 * @param {string} token - The authorization token for the request.
 * @returns {Promise} A promise that resolves with the response from the API, or rejects with an error message if no token is provided.
 */
export const sendSMSv4 = (phone, token) => {
  if (!token)
    return new Promise((resolve, reject) => {
      return reject('No token')
    })
  return axios.post(
    '/v4/pincode/send/sms',
    {
      phone,
      platform,
    },
    {
      withCredentials: false,
      headers: {
        Authorization: token,
      },
    }
  )
}

/**
 * Initiates a phone validation call by sending a POST request to the server.
 *
 * @param {string} phone - The phone number to validate.
 * @returns {Promise} Axios promise resolving to the server response.
 */
export const makeCall = phone =>
  axios.post(
    '/v2/pincode/make/call',
    {
      phone,
      platform,
    },
    {
      withCredentials: true,
    }
  )

/**
 * Initiates a phone validation call using the v4 API.
 *
 * @param {string} phone - The phone number to validate.
 * @param {string} token - The authorization token for the request.
 * @returns {Promise} Resolves with the API response or rejects with an error message if no token is provided.
 */
export const makeCallv4 = (phone, token) => {
  if (!token)
    return new Promise((resolve, reject) => {
      return reject('No token')
    })
  return axios.post(
    '/v3/pincode/make/call',
    {
      phone,
      platform,
    },
    {
      withCredentials: false,
      headers: {
        Authorization: token,
      },
    }
  )
}

/**
 * Sends a request to verify a phone validation code.
 *
 * @param {string} phone - The phone number to validate.
 * @param {string} code - The validation code to check.
 * @returns {Promise<Object>} A promise that resolves with the response data from the API.
 */
export const checkCode = (phone, code) =>
  axios
    .post(
      '/v1/pincode/check',
      {
        phone,
        code,
        platform,
        attemptsCheck: true,
      },
      {
        withCredentials: true,
      }
    )
    .then(({ data }) => {
      return data
    })

/**
 * Verifies a phone pincode using the v4 API endpoint.
 *
 * @param {string} phone - The phone number to verify.
 * @param {string} code - The verification code sent to the phone.
 * @param {string} token - The authentication token required for the request.
 * @param {string} abTests - AB test group identifiers for the request header.
 * @returns {Promise<Object>} Resolves with the response data if successful, or rejects with an error message.
 */
export const checkCodev4 = (phone, code, token, abTests) => {
  if (!token)
    return new Promise((resolve, reject) => {
      return reject('No token')
    })
  return axios
    .post(
      '/v2/user/login/verify_pincode',
      {
        phone,
        code,
        platform,
        token,
        type: 'PHONE',
      },
      {
        headers: {
          'X-TestAB-Groups': abTests,
        },
      }
    )
    .then(({ data }) => {
      return data
    })
}

export const checkCodeV3 = (phone, code) =>
  axios.post(
    '/v3/pincode/check',
    {
      type: 'CUSTOM-PHONE',
      code,
      key: phone,
      platform,
    },
    {
      withCredentials: true,
    }
  )

export const phoneOverride = (phone, code) =>
  axios.post(
    '/v1/phone/override',
    {
      phone,
      code,
      platform,
    },
    {
      withCredentials: true,
    }
  )

export const phoneOverridev4 = (phone, code, token, abTests) => {
  if (!token)
    return new Promise((resolve, reject) => {
      return reject('No token')
    })
  return axios.post(
    '/v2/user/login/phone_override',
    {
      phone,
      code,
      platform,
      token,
    },
    {
      headers: {
        'X-TestAB-Groups': abTests,
      },
    }
  )
}

export const phoneCheckReceivedCode = phone =>
  axios.post(
    '/v1/attempts/phone/check',
    {
      phone,
      platform,
    },
    {
      withCredentials: true,
    }
  )

export const phoneCheckReceivedCodev4 = (phone, token) => {
  if (!token)
    return new Promise((resolve, reject) => {
      return reject('No token')
    })
  return axios.post(
    '/v2/attempts/phone/check',
    {
      phone,
      platform,
    },
    {
      withCredentials: false,
      headers: {
        Authorization: token,
      },
    }
  )
}

export const sendCode2FA = (type, issuer, token) =>
  axios.post(
    '/v3/pincode/send',
    {
      type,
      issuer,
    },
    {
      headers: {
        Authorization: token,
      },
      withCredentials: false,
    }
  )

export const userChallenges = () => {
  return axios.get('/challenges/flow/2fa-selfservice', {
    withCredentials: true,
    baseURL: 'https://tnv-pradinha.olx.com.br',
  })
}
